import * as React from 'react';

import { PillBadge } from '@lightspeed/flame/Badge';
import { Bone } from '@lightspeed/flame/Bone';
import { Box } from '@lightspeed/flame/Core';
import { IconCheckmark } from '@lightspeed/flame/Icon/Checkmark';
import { IconDanger } from '@lightspeed/flame/Icon/Danger';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import { Tab, TabContainer } from '@lightspeed/flame/Tab';
import { Text } from '@lightspeed/flame/Text';

import {
  ItemReadiness,
  RowDataSortable,
  TableFilter,
} from 'root/apps/PurchaseOrder/NuorderOrdering/types';

interface OrderLineTabProps {
  onSetFilters: (filters: TableFilter<RowDataSortable>[]) => void;
  counts: {
    all: number;
    error: number;
    warning: number;
    ready: number;
  };
  isLoading?: boolean;
}

export const OrderLineTabs: React.FC<OrderLineTabProps> = ({
  onSetFilters,
  counts,
  isLoading = false,
}) => {
  const [state, setState] = React.useState(0);

  if (isLoading) {
    return (
      <TabContainer data-test="bonesTabs">
        <Tab>
          <Bone width={50} height={20} />
        </Tab>
        <Tab>
          <Bone width={50} height={20} />
        </Tab>
        <Tab>
          <Bone width={50} height={20} />
        </Tab>
        <Tab>
          <Bone width={50} height={20} />
        </Tab>
      </TabContainer>
    );
  }

  return (
    <TabContainer>
      <Tab
        onClick={() => {
          setState(0);
          onSetFilters([]);
        }}
        active={state === 0}
        data-test="allTab"
      >
        All
        <CountPill value={counts.all} active={state === 0} data-test="allCount" />
      </Tab>
      <Tab
        onClick={() => {
          setState(1);
          onSetFilters([
            {
              key: 'itemReadiness',
              values: [ItemReadiness.NO_UPC, ItemReadiness.NO_UUID, ItemReadiness.NOT_ON_NUORDER],
            },
          ]);
        }}
        active={state === 1}
        data-test="errorTab"
      >
        <IconDanger />
        <Text ml={1}>Errors</Text>
        <CountPill value={counts.error} active={state === 1} data-test="errorCount" />
      </Tab>
      <Tab
        onClick={() => {
          setState(2);
          onSetFilters([
            {
              key: 'itemReadiness',
              values: [
                ItemReadiness.NO_AVAILABILITY,
                ItemReadiness.OUT_OF_STOCK,
                ItemReadiness.OVER_ORDERED,
              ],
            },
          ]);
        }}
        active={state === 2}
        data-test="warningTab"
      >
        <IconWarning />
        <Text ml={1}>Warnings</Text>
        <CountPill value={counts.warning} active={state === 2} data-test="warningCount" />
      </Tab>
      <Tab
        onClick={() => {
          setState(3);
          onSetFilters([
            {
              key: 'itemReadiness',
              values: [ItemReadiness.NOT_READY, ItemReadiness.READY],
            },
          ]);
        }}
        active={state === 3}
        data-test="readyTab"
      >
        <IconCheckmark />
        <Text ml={1}>Ready</Text>
        <CountPill value={counts.ready} active={state === 3} data-test="readyCount" />
      </Tab>
    </TabContainer>
  );
};

const CountPill: React.FC<{ value: number; active: boolean }> = ({ value, active, ...rest }) => {
  return (
    <Box ml={1}>
      <PillBadge size="small" type={active ? 'info' : 'default'} {...rest}>
        <var>{value}</var>
      </PillBadge>
    </Box>
  );
};
